import unescapedText from 'I18n/utils/unescapedText';
import { getActionText } from 'copilot-toolkit/actions/getActionText';
import { parseInAppCopilotAction } from 'copilot-toolkit/actions/parseInAppCopilotAction';
import { CALL_SUMMARY, FEEDBACK_SUBMISSION_SUMMARY, OBJECT_ENGAGEMENTS_SUMMARY, OBJECT_SUMMARY } from 'copilot-toolkit/constants/CopilotInAppActionTypes';
const getDefaultMessage = () => {
  return unescapedText('aiAddonCopilotPreview.components.copilotV2.defaultMessage');
};

// This is added to support the property_filters field in the V2 chirp API, this is kind of a hack to get the FEEDBACK_SUMMARY to work

/**
 * Converts snake_case string to camelCase
 * @param str - The snake_case string to convert
 * @returns The camelCase version of the string
 */
const toCamelCase = str => {
  return str.replace(/_+/g, '_') // Replace multiple consecutive underscores with single underscore
  .replace(/_([a-zA-Z])/g, (_, letter) => letter.toUpperCase()) // Convert _letter to Letter
  .replace(/^_+|_+$/g, ''); // Remove leading and trailing underscores
};

/**
 * Converts snake_case keys to camelCase in property_filters
 * @param propertyFilters - Array of property filter objects
 * @returns Array with camelCase keys
 */

const convertPropertyFiltersKeysToCamelCase = propertyFilters => {
  return propertyFilters.map(filter => {
    const convertedFilter = {};
    Object.entries(filter).forEach(([key, value]) => {
      const camelCaseKey = toCamelCase(key);
      if (Array.isArray(value)) {
        // If value is an array
        convertedFilter[camelCaseKey] = value;
      } else if (typeof value === 'object' && value !== null) {
        // If value is an object, convert its keys to camelCase as well
        const convertedValue = {};
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          if (Array.isArray(nestedValue)) {
            // If nested value is an array, keep it as an array
            convertedValue[toCamelCase(nestedKey)] = nestedValue;
          } else {
            convertedValue[toCamelCase(nestedKey)] = nestedValue;
          }
        });
        convertedFilter[camelCaseKey] = convertedValue;
      } else {
        convertedFilter[camelCaseKey] = value;
      }
    });
    return convertedFilter;
  });
};

/**
 * Creates property filters for date range filtering
 * @param fromDate - Optional start date in ISO string format
 * @param toDate - Optional end date in ISO string format
 * @returns Array of property filter objects for date range, or undefined if no dates provided
 */
const createDateRangePropertyFilters = (fromDate, toDate) => {
  const filters = [];
  if (fromDate) {
    filters.push({
      property: 'from_date',
      operator: 'EQ',
      value: fromDate
    });
  }
  if (toDate) {
    filters.push({
      property: 'to_date',
      operator: 'EQ',
      value: toDate
    });
  }
  return filters.length > 0 ? filters : undefined;
};

/**
 * Determines the summary type based on object type ID or object type
 * @param objectTypeId - The object type ID (e.g., '0-48', '0-5')
 * @returns The corresponding SummaryType
 */
const getSummaryTypeFromObjectType = objectTypeId => {
  if (objectTypeId === '0-48' || objectTypeId === 'CALL' || objectTypeId === 'call') {
    return 'CALL';
  } else if (objectTypeId === '0-5' || objectTypeId === 'TICKET' || objectTypeId === 'ticket') {
    return 'TICKET';
  }
  return 'STANDARD';
};

/**
 * Maps data from V1 format to V2 format for Copilot Preview
 * Handles specific intents: object_summary and object_engagement_summary
 * @param copilotAction The V1 format copilot action
 * @returns Mapped data with label and object information for V2 API
 */
export const getMapV1DataToV2 = (copilotAction, summaryType) => {
  const messageAction = parseInAppCopilotAction(copilotAction);
  let previewSummaryType = 'STANDARD';
  let ticketThreadId = undefined;
  switch (copilotAction.intent) {
    case OBJECT_SUMMARY:
      {
        previewSummaryType = summaryType || getSummaryTypeFromObjectType(copilotAction.objectTypeId);
        const dateRangePropertyFilters = createDateRangePropertyFilters(copilotAction.fromDate, copilotAction.toDate);
        if (copilotAction.context && 'threadId' in copilotAction.context) {
          ticketThreadId = Number(copilotAction.context.threadId);
        }
        return {
          message: copilotAction.label || messageAction && getActionText(messageAction) || getDefaultMessage(),
          context: {
            crmObjectContext: {
              objectTypeId: copilotAction.objectTypeId,
              objectId: Number(copilotAction.objectId)
            }
          },
          ticketThreadId,
          summaryType: previewSummaryType,
          propertyFilters: dateRangePropertyFilters ? JSON.stringify(dateRangePropertyFilters) : undefined
        };
      }
    case OBJECT_ENGAGEMENTS_SUMMARY:
      previewSummaryType = summaryType || getSummaryTypeFromObjectType(copilotAction.objectType);
      if (copilotAction.context && 'threadId' in copilotAction.context) {
        ticketThreadId = Number(copilotAction.context.threadId);
      }
      return {
        message: copilotAction.text || messageAction && getActionText(messageAction) || getDefaultMessage(),
        context: {
          crmObjectContext: {
            objectTypeId: copilotAction.objectType,
            objectId: Number(copilotAction.objectId)
          }
        },
        ticketThreadId,
        summaryType: previewSummaryType
      };
    case CALL_SUMMARY:
      return {
        message: getDefaultMessage(),
        context: {
          crmObjectContext: {
            objectTypeId: copilotAction.objectTypeId,
            objectId: Number(copilotAction.objectId)
          }
        },
        summaryType: 'CALL'
      };
    case FEEDBACK_SUBMISSION_SUMMARY:
      {
        var _copilotAction$proper, _copilotAction$contex;
        const objectId = (_copilotAction$proper = copilotAction.property_filters) === null || _copilotAction$proper === void 0 || (_copilotAction$proper = _copilotAction$proper.find(filter => filter.property === 'hs_survey_crm_object_id')) === null || _copilotAction$proper === void 0 ? void 0 : _copilotAction$proper.value;
        const convertedPropertyFilters = copilotAction.property_filters ? convertPropertyFiltersKeysToCamelCase(copilotAction.property_filters) : undefined;
        const summaryFormat = (copilotAction === null || copilotAction === void 0 || (_copilotAction$contex = copilotAction.context) === null || _copilotAction$contex === void 0 ? void 0 : _copilotAction$contex.summaryMode) === 'MEAL_SIZED' ? 'STANDARD' : 'PREVIEW';
        return {
          message: copilotAction.text || getDefaultMessage(),
          context: {
            crmObjectContext: {
              objectTypeId: '0-111',
              objectId: objectId ? Number(objectId) : -1 // using -1 to indicate that the object id is not available since this is a required prop
            }
          },
          propertyFilters: convertedPropertyFilters ? JSON.stringify(convertedPropertyFilters) : undefined,
          summaryType: 'FEEDBACK_SUBMISSION',
          summaryFormat
        };
      }
    default:
      return {
        message: getDefaultMessage(),
        context: {
          crmObjectContext: undefined
        },
        summaryType: previewSummaryType
      };
  }
};